import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { API_URL } from '../../utils/config';
import { getImageSrc } from '../../utils/imageHelper';
import { getColorFromName } from '../../utils/colorUtils';

// Helper function to create matte/muted version of colors - same as in ProductColorAttributes
const createMatteColor = (hexColor) => {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Mute the color by mixing with gray and reducing saturation
  const matteR = Math.floor(r * 0.8 + 50);
  const matteG = Math.floor(g * 0.8 + 50);
  const matteB = Math.floor(b * 0.8 + 50);
  
  // Convert back to hex
  return `#${matteR.toString(16).padStart(2, '0')}${matteG.toString(16).padStart(2, '0')}${matteB.toString(16).padStart(2, '0')}`;
};

const ProductListColorOptions = ({ productId, colors, maxColors, disableLinks = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [colorAttributes, setColorAttributes] = useState(() => (
    Array.isArray(colors) ? colors : []
  ));
  const [loading, setLoading] = useState(!Array.isArray(colors));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (Array.isArray(colors)) {
      setColorAttributes(colors);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let ignore = false;

    const fetchColorAttributes = async () => {
      if (!productId) return;
      
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/products/colors/${productId}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch color attributes');
        }
        
        // Get the response text
        const text = await response.text();
        
        // Try to parse the JSON
        let data = [];
        try {
          // If the response is valid JSON, parse it directly
          data = JSON.parse(text);
        } catch (parseError) {
          console.error('Error parsing JSON:', parseError);
          
          // If parsing fails, try to fix the JSON by adding missing commas and brackets
          try {
            // Add missing commas between objects
            const fixedText = '[' + text.replace(/}{/g, '},{') + ']';
            data = JSON.parse(fixedText);
          } catch (fixError) {
            console.error('Error parsing fixed JSON:', fixError);
            throw new Error('Invalid JSON response from server');
          }
        }
        
        if (!ignore) {
          setColorAttributes(data);
        }
      } catch (error) {
        console.error('Error fetching color attributes:', error);
        if (!ignore) {
          setError(error.message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchColorAttributes();

    return () => {
      ignore = true;
    };
  }, [colors, productId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (error || colorAttributes.length === 0) {
    return null; // Don't show anything if there's an error or no colors
  }

  const effectiveMaxColors = maxColors ?? (isMobile ? 5 : 10);
  const hasMoreColors = colorAttributes.length > effectiveMaxColors;
  const displayColors = colorAttributes.slice(0, effectiveMaxColors);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
      {displayColors.map((attribute) => (
        <Tooltip key={attribute.id} title={attribute.title} arrow>
          <Box 
            component={!disableLinks && attribute.link ? RouterLink : 'div'}
            to={!disableLinks && attribute.link ? attribute.link : undefined}
            sx={{ 
              width: 20,
              height: 20,
              borderRadius: '50%',
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
              opacity: 0.9, // Slightly reduce opacity for matte look
              boxShadow: 'inset 0 0 3px rgba(0, 0, 0, 0.1)' // Add subtle inner shadow
            }}
          >
            {attribute.image ? (
              <img 
                src={getImageSrc(attribute.image, 'thumbs')}
                alt={attribute.title}
                loading="lazy"
                decoding="async"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'saturate(0.85)' // Reduce saturation of images
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (theme) => {
                    // Get matte version of the color
                    const originalColor = getColorFromName(attribute.title);
                    return createMatteColor(originalColor);
                  }
                }}
              />
            )}
          </Box>
        </Tooltip>
      ))}
      
      {hasMoreColors && (
        <Tooltip title={`${colorAttributes.length - effectiveMaxColors} more colors`} arrow>
          <Box 
            sx={{ 
              minWidth: 24,
              height: 20,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#e6e6e6', // Slightly darker gray
              border: '1px solid #d0d0d0',
              px: 0.25,
              fontSize: '0.65rem',
              fontWeight: 600,
              opacity: 0.9 // Match the matte look
            }}
          >
            +{colorAttributes.length - effectiveMaxColors}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default ProductListColorOptions;
